import React, {Component} from 'react';
import {action, observable, computed, autorun} from 'mobx';
import {inject, observer} from 'mobx-react';
import {
  Button, FormInput, FormSelect, FormCheckbox, FormTextarea,
  Modal, ModalBody, ModalHeader, ModalFooter
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import moment from 'moment';
import PageTitle from '../../../components/common/PageTitle';
import {
  DeviceFrame, DEVICE_FRAMES, POPUP_TYPES, POPUP_POSITIONS_BY_TYPE,
  TEMPLATE_PRESETS, defaultTemplateData
} from './popupPreview';
import './popup.scss';

const PAGES = [
  {value: 'main',     label: '메인'},
  {value: 'category', label: '카테고리'},
  {value: 'product',  label: '상품상세'},
  {value: 'cart',     label: '장바구니'},
  {value: 'order',    label: '결제'},
  {value: 'register', label: '회원가입'},
  {value: 'mypage',   label: '마이페이지'}
];

const STATUS_FILTERS = [
  {value: '',          label: '전체'},
  {value: 'active',    label: '노출중'},
  {value: 'scheduled', label: '예약'},
  {value: 'expired',   label: '종료'},
  {value: 'inactive',  label: '비활성'}
];

const TRIGGERS = [
  {value: 'pageLoad',   label: '페이지 진입시 (즉시)'},
  {value: 'delay',      label: 'N초 후 진입'},
  {value: 'scroll',     label: '스크롤 N% 도달시'},
  {value: 'exitIntent', label: '마우스 이탈 감지 (Exit Intent)'},
  {value: 'click',      label: '특정 요소 클릭시'}
];

const FREQUENCIES = [
  {value: 'once',     label: '1회만 (영구)'},
  {value: 'daily',    label: '매일 1회'},
  {value: 'session',  label: '세션당 1회'},
  {value: 'interval', label: 'N시간마다'},
  {value: 'always',   label: '항상 (매번)'}
];

const DEFAULT_POPUP = () => ({
  title: '',
  description: '',
  type: 'modal',
  position: 'center',
  width:  {value: 480, unit: 'px'},
  height: {value: 0,   unit: 'px'},
  bgColor: '#FFFFFF',
  fgColor: '#1F2937',
  accentColor: '#4A6CF7',
  borderRadius: 12,
  shadow: 'lg',
  templateKey: 'text',
  templateData: defaultTemplateData('text'),
  elements: [],
  display: {pages: [], customUrls: [], devices: ['pc', 'mobile', 'tablet'], audience: 'all'},
  trigger: {type: 'pageLoad', delayMs: 3000, scrollPercent: 50, selector: ''},
  frequency: {mode: 'session', intervalHours: 4, showDismissBtn: true, dismissDays: 1},
  startAt: null,
  endAt: null,
  isActive: true
});

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({common}) => ({
  getPopupList: common.getPopupList,
  savePopup: common.savePopup,
  removePopup: common.removePopup,
  duplicatePopup: common.duplicatePopup,
  togglePopup: common.togglePopup,
  reorderPopups: common.reorderPopups,
  base64UploadImages: common.base64UploadImages
}))
@observer
class Popup extends Component {

  @observable store = {};
  @observable list = [];
  @observable totalCount = 0;
  @observable page = 1;
  @observable totalPages = 1;
  @observable limit = 30;

  @observable filterStatus = '';
  @observable filterPosition = '';
  @observable keyword = '';

  @observable isLoading = false;
  @observable isSaving = false;

  @observable editingPopup = null;
  @observable editTabIndex = 0;
  @observable showEditModal = false;
  @observable previewDevice = 'pc';
  // 노출 위치 지정 방식 — 'page'(페이지 유형으로 지정) | 'url'(주소로 직접 지정)
  // 위젯은 pages·customUrls 를 AND 로 묶으므로 둘을 동시에 쓰면 노출이 안 될 수 있음 → 하나만 사용하도록 분리
  @observable displayLocationMode = 'page';
  // 직접 지정 URL(노출 위치) 입력 UX 상태
  @observable customUrlDraft = '';       // 입력 중인 주소
  @observable customUrlIncludeSub = false; // "하위 페이지 전체 포함" 체크(내부적으로 * 부착)
  @observable customUrlError = '';       // 입력 검증 에러 메시지
  // 편집 모드 — 'simple' (필수 필드만) | 'advanced' (전체 탭)
  // 사용자 요청: 모달 진입 시 항상 쉬운 모드로 시작
  @observable editMode = 'simple';

  searchTimer = null;
  areaRef = React.createRef();
  _areaResizeObserver = null;
  fileInputRef = React.createRef();

  @observable isUploading = false;

  @observable areaW = 0;
  @observable areaH = 0;

  PERSIST_KEY = 'popup_editingDraft';

  componentDidMount() {
    if (this.props.me) this.store = this.props.me.store ? this.props.me.store : {};
    this.reload();
    // 탭 전환 등으로 컴포넌트가 unmount/remount될 때 모달 작업 상태 복원
    try {
      const saved = window.sessionStorage && window.sessionStorage.getItem(this.PERSIST_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.showEditModal && parsed.editingPopup) {
          this.editingPopup = parsed.editingPopup;
          this.showEditModal = true;
          if (parsed.editMode) this.editMode = parsed.editMode;
          if (typeof parsed.editTabIndex === 'number') this.editTabIndex = parsed.editTabIndex;
          if (parsed.previewDevice) this.previewDevice = parsed.previewDevice;
        }
      }
    } catch (e) { /* ignore */ }

    // 편집 상태 변경마다 sessionStorage에 자동 저장
    this._persistDisposer = autorun(() => {
      try {
        if (this.showEditModal && this.editingPopup) {
          window.sessionStorage && window.sessionStorage.setItem(this.PERSIST_KEY, JSON.stringify({
            showEditModal: true,
            editingPopup: this.editingPopup,
            editMode: this.editMode,
            editTabIndex: this.editTabIndex,
            previewDevice: this.previewDevice,
          }));
        } else {
          window.sessionStorage && window.sessionStorage.removeItem(this.PERSIST_KEY);
        }
      } catch (e) { /* ignore quota / privacy mode */ }
    });
  }

  componentWillUnmount() {
    if (this._areaResizeObserver) { try { this._areaResizeObserver.disconnect(); } catch (e) {} }
    if (this._persistDisposer) { try { this._persistDisposer(); } catch (e) {} }
  }

  // 미리보기 stage가 마운트되면 실제 폭/높이 측정 → previewScale에 반영
  attachAreaRef = (el) => {
    this.areaRef.current = el;
    if (this._areaResizeObserver) { try { this._areaResizeObserver.disconnect(); } catch (e) {} }
    if (!el || typeof window === 'undefined' || typeof window.ResizeObserver === 'undefined') {
      if (el) {
        this.areaW = el.clientWidth;
        this.areaH = el.clientHeight;
      }
      return;
    }
    this._areaResizeObserver = new window.ResizeObserver((entries) => {
      const r = entries[0];
      if (r) {
        this.areaW = r.contentRect.width;
        this.areaH = r.contentRect.height;
      }
    });
    this._areaResizeObserver.observe(el);
    // 초기값
    this.areaW = el.clientWidth;
    this.areaH = el.clientHeight;
  };

  @computed get hasStore() { return !!(this.store && this.store._id); }

  @action reload = async () => {
    if (!this.hasStore) return;
    this.isLoading = true;
    try {
      const res = await this.props.getPopupList({
        store_id: this.store._id,
        shopNo: this.store.shopNo || 1,
        page: this.page, limit: this.limit,
        keyword: this.keyword || '',
        position: this.filterPosition || '',
        status: this.filterStatus || ''
      });
      if (res && res.data) {
        this.list = Array.isArray(res.data.docs) ? res.data.docs : [];
        this.totalCount = res.data.totalDocs || this.list.length;
        this.totalPages = res.data.totalPages || 1;
      }
    } catch (e) { toast.error('팝업 목록 조회 실패'); }
    finally { this.isLoading = false; }
  };

  @action onKeywordChange = (e) => {
    this.keyword = e.target.value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => { this.page = 1; this.reload(); }, 300);
  };

  @action setStatusFilter = (s) => { this.filterStatus = s; this.page = 1; this.reload(); };
  @action goPage = (p) => { if (p < 1 || p > this.totalPages) return; this.page = p; this.reload(); };

  @action setEditMode = (mode) => {
    this.editMode = mode;
  };

  // 이미지 파일 업로드 (Cafe24 → CDN) — 성공 시 templateData.imageUrl 에 저장
  triggerImageUpload = () => {
    if (this.fileInputRef.current) this.fileInputRef.current.click();
  };

  @action handleImageUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    // input 초기화 — 같은 파일 재업로드 가능하도록
    if (event.target) event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('5MB 이하 이미지만 업로드할 수 있습니다.');
      return;
    }
    const me = this.props.me || {};
    const store_id = me.selStore_id || (me.store && me.store._id);
    if (!store_id) {
      toast.error('스토어 정보를 확인할 수 없습니다.');
      return;
    }

    this.isUploading = true;
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      this.props.base64UploadImages({
        store_id,
        base64Imgs: [{ image: base64 }]
      }, (res) => {
        this.isUploading = false;
        if (res && res.errors && res.errors.length > 0) {
          toast.error(res.errors[0].message || '이미지 업로드에 실패했습니다.');
          return;
        }
        // Cafe24 응답: { images: [ { path, name } ] } 또는 string array (호환)
        const item = res && res.images && res.images[0];
        let imgUrl = null;
        if (typeof item === 'string') {
          imgUrl = item;
        } else if (item && typeof item === 'object') {
          imgUrl = item.path || item.url || item.original || item.src || item.image_url || null;
        }
        if (imgUrl) {
          this.setTemplateData('imageUrl', imgUrl);
          toast.success('이미지가 업로드되었습니다.');
        } else {
          console.warn('이미지 업로드 응답:', res);
          toast.error('이미지 URL을 추출하지 못했습니다. 응답 형식을 확인해 주세요.');
        }
      });
    };
    reader.onerror = () => {
      this.isUploading = false;
      toast.error('파일을 읽는 중 오류가 발생했습니다.');
    };
    reader.readAsDataURL(file);
  };

  // === 모달 ===
  @action openCreate = () => {
    this.editingPopup = DEFAULT_POPUP();
    // 쉬운 모드 기본값: 유형=중앙 모달, 콘텐츠=이미지
    this.editingPopup.type = 'modal';
    this.editingPopup.position = 'center';
    this.editingPopup.templateKey = 'image';
    this.editingPopup.templateData = defaultTemplateData('image');
    this.editTabIndex = 0;
    this.editMode = 'simple';
    this.displayLocationMode = 'page';
    this.resetCustomUrlInput();
    this.showEditModal = true;
  };

  // 쉬운 모드로 표현 불가능한 심화 설정이 하나라도 있는지 — openEdit 시 기본 모드 결정에 사용
  isAdvancedPopup(p) {
    if (!p) return false;
    const d = p.display || {};
    const t = p.trigger || {};
    const f = p.frequency || {};
    if (p.type && p.type !== 'modal') return true;                            // 유형(중앙 모달 외)
    if (p.templateKey && p.templateKey !== 'image') return true;              // 콘텐츠 템플릿(이미지 외)
    if (Array.isArray(d.customUrls) && d.customUrls.length > 0) return true;  // 직접 지정 URL
    const pages = d.pages || [];                                             // 쉬운 프리셋(all/main/product) 외 페이지
    const simplePages = pages.length === 0 || (pages.length === 1 && (pages[0] === 'main' || pages[0] === 'product'));
    if (!simplePages) return true;
    const devices = d.devices || [];                                        // 디바이스 일부만 선택
    if (devices.length > 0 && devices.length < 3) return true;
    if (d.audience && d.audience !== 'all') return true;                    // 회원 조건
    if (t.type && t.type !== 'pageLoad') return true;                       // 트리거(즉시 노출 외)
    if (f.mode && f.mode !== 'session') return true;                        // 빈도(세션당 1회 외)
    return false;
  }

  @action openEdit = (p) => {
    this.editingPopup = JSON.parse(JSON.stringify(p));
    const def = DEFAULT_POPUP();
    if (!this.editingPopup.width)  this.editingPopup.width = def.width;
    if (!this.editingPopup.height) this.editingPopup.height = def.height;
    if (!this.editingPopup.display) this.editingPopup.display = def.display;
    if (!this.editingPopup.trigger) this.editingPopup.trigger = def.trigger;
    if (!this.editingPopup.frequency) this.editingPopup.frequency = def.frequency;
    if (!this.editingPopup.templateData) this.editingPopup.templateData = defaultTemplateData(this.editingPopup.templateKey || 'text');
    this.editTabIndex = 0;
    // 심화 설정이 하나라도 있으면(=심화 모드에서 설정한 팝업) 심화 모드로 진입, 아니면 쉬운 모드
    this.editMode = this.isAdvancedPopup(this.editingPopup) ? 'advanced' : 'simple';
    // 직접 지정 URL 이 하나라도 있으면 '주소로 직접 지정' 방식으로 진입
    this.displayLocationMode = ((this.editingPopup.display.customUrls || []).length > 0) ? 'url' : 'page';
    this.resetCustomUrlInput();
    this.showEditModal = true;
  };

  @action closeEdit = () => {
    this.showEditModal = false;
    this.editingPopup = null;
    this.resetCustomUrlInput();
  };

  @action resetCustomUrlInput = () => {
    this.customUrlDraft = '';
    this.customUrlIncludeSub = false;
    this.customUrlError = '';
  };

  // 노출 위치 지정 방식 전환 — 하나만 사용하도록 상대편 값을 비워 위젯 AND 충돌을 원천 차단
  @action setDisplayLocationMode = (mode) => {
    if (this.displayLocationMode === mode) return;
    this.displayLocationMode = mode;
    if (!this.editingPopup.display) this.editingPopup.display = {};
    if (mode === 'page') {
      this.setNested('display', 'customUrls', []); // 주소 방식 값 제거
      this.resetCustomUrlInput();
    } else {
      this.setNested('display', 'pages', []);       // 페이지 유형 선택 제거
    }
  };

  @action setField = (key, value) => {
    if (!this.editingPopup) return;
    this.editingPopup[key] = value;
  };
  @action setNested = (group, key, value) => {
    if (!this.editingPopup[group]) this.editingPopup[group] = {};
    this.editingPopup[group][key] = value;
  };
  @action setSize = (target, key, value) => {
    if (!this.editingPopup[target]) this.editingPopup[target] = {value: 0, unit: 'px'};
    this.editingPopup[target][key] = value;
  };
  @action setTemplateKey = (key) => {
    this.editingPopup.templateKey = key;
    this.editingPopup.templateData = defaultTemplateData(key);
  };
  @action setTemplateData = (key, value) => {
    if (!this.editingPopup.templateData) this.editingPopup.templateData = {};
    this.editingPopup.templateData[key] = value;
  };
  @action setType = (type) => {
    this.editingPopup.type = type;
    // 위치 자동 보정
    const positions = POPUP_POSITIONS_BY_TYPE[type] || [{v: 'center'}];
    if (!positions.find(p => p.v === this.editingPopup.position)) {
      this.editingPopup.position = positions[0].v;
    }
  };
  @action togglePage = (p) => {
    const cur = this.editingPopup.display.pages || [];
    if (cur.includes(p)) this.editingPopup.display.pages = cur.filter(x => x !== p);
    else this.editingPopup.display.pages = [...cur, p];
  };
  @action toggleDevice = (d) => {
    const cur = this.editingPopup.display.devices || [];
    if (cur.includes(d)) this.editingPopup.display.devices = cur.filter(x => x !== d);
    else this.editingPopup.display.devices = [...cur, d];
  };

  // ---- 직접 지정 URL(노출 위치) ----------------------------------------
  // 위젯은 현재 페이지의 "경로(pathname)+쿼리"만 비교한다. 즉 https://쇼핑몰주소 같은
  // 도메인을 넣으면 절대 매칭되지 않는다. 사용자가 브라우저 주소창의 전체 URL을 그대로
  // 붙여넣는 실수가 잦아, 입력을 자동 정리하고 문제가 있으면 안내한다.
  // 반환: { ok, value, error, autoFixed }
  normalizeCustomUrl(raw) {
    let s = String(raw == null ? '' : raw).trim();
    if (!s) return {ok: false, error: '노출할 페이지 주소를 입력해 주세요.'};
    let autoFixed = false;

    // 1) 전체 URL(http://, https://, //) → 경로만 추출
    if (/^(https?:)?\/\//i.test(s)) {
      try {
        const u = new URL(/^\/\//.test(s) ? 'http:' + s : s);
        s = (u.pathname || '/') + (u.search || '');
        autoFixed = true;
      } catch (e) {
        // 파싱 실패 시 첫 슬래시 이후만 사용
        const rest = s.replace(/^(https?:)?\/\//i, '');
        const idx = rest.indexOf('/');
        s = idx >= 0 ? rest.slice(idx) : '';
        autoFixed = true;
      }
    } else {
      // 2) 프로토콜 없는 도메인(www.mall.com/event, mall.co.kr/event) → 경로만 추출
      const first = s.split('/')[0];
      const isFile = /\.(html?|php|aspx?|jsp)$/i.test(first);           // detail.html 같은 파일명은 도메인 아님
      const looksDomain = /^(www\.)?([a-z0-9-]+\.)+(com|net|org|co|kr|io|me|biz|shop|store|cafe24)(\.[a-z]{2,3})?$/i.test(first);
      if (!isFile && looksDomain) {
        const idx = s.indexOf('/');
        s = idx >= 0 ? s.slice(idx) : '';
        autoFixed = true;
      }
    }

    // 공백 제거
    if (/\s/.test(s)) { s = s.replace(/\s+/g, ''); autoFixed = true; }

    // 경로가 비었으면(도메인만 붙여넣은 경우) 모든 페이지에 노출되므로 막는다
    if (!s || s === '/') {
      return {ok: false, error: '페이지 주소가 없습니다. "/event", "/event/2026" 처럼 쇼핑몰 주소 뒤의 경로만 입력해 주세요. (전체 노출은 위의 "노출 페이지"를 사용하세요.)'};
    }

    // 반드시 "/" 로 시작 (와일드카드 * 로 시작하는 특수 케이스는 허용)
    if (s[0] !== '/' && s[0] !== '*') { s = '/' + s; autoFixed = true; }

    // "*" / "/*" 처럼 사실상 모든 페이지를 의미하면 막는다(전체 노출은 "노출 페이지"에서)
    if (/^\/?\*+$/.test(s)) {
      return {ok: false, error: '이 주소는 모든 페이지에 노출돼요. 전체 노출은 위의 "노출 페이지"를 비워두세요.'};
    }

    return {ok: true, value: s, autoFixed};
  }

  // 기존에 저장된 값(칩)이 잘못됐는지 판별 → 에러 표시용. null이면 정상.
  customUrlIssue(url) {
    const s = String(url == null ? '' : url);
    if (/^(https?:)?\/\//i.test(s)) return '쇼핑몰 주소(도메인)가 포함되어 있어 노출되지 않습니다';
    const first = s.split('/')[0];
    const isFile = /\.(html?|php|aspx?|jsp)$/i.test(first);
    const looksDomain = /^(www\.)?([a-z0-9-]+\.)+(com|net|org|co|kr|io|me|biz|shop|store|cafe24)(\.[a-z]{2,3})?$/i.test(first);
    if (!s.startsWith('/') && !s.startsWith('*') && looksDomain && !isFile) return '쇼핑몰 주소(도메인)가 포함되어 있어 노출되지 않습니다';
    if (!s.startsWith('/') && !s.startsWith('*')) return '주소는 "/" 로 시작해야 합니다';
    if (/\s/.test(s)) return '주소에 공백이 있습니다';
    return null;
  }

  @action setCustomUrlDraft = (v) => {
    this.customUrlDraft = v;
    if (this.customUrlError) this.customUrlError = '';
  };

  @action toggleCustomUrlIncludeSub = () => {
    this.customUrlIncludeSub = !this.customUrlIncludeSub;
  };

  // 입력창의 주소를 목록(customUrls)에 추가
  @action addCustomUrl = () => {
    const res = this.normalizeCustomUrl(this.customUrlDraft);
    if (!res.ok) { this.customUrlError = res.error; return; }
    let value = res.value;
    // "하위 페이지 전체 포함" 체크 시 * 부착 (사용자에게 와일드카드 용어를 노출하지 않음)
    if (this.customUrlIncludeSub && !value.endsWith('*')) value = value + '*';
    const cur = (this.editingPopup.display && this.editingPopup.display.customUrls) || [];
    if (cur.includes(value)) {
      this.customUrlError = '이미 추가된 주소입니다.';
      return;
    }
    this.setNested('display', 'customUrls', [...cur, value]);
    this.customUrlDraft = '';
    this.customUrlError = '';
    this.customUrlIncludeSub = false;
    if (res.autoFixed) toast.info('쇼핑몰 주소는 빼고 "' + value + '" 경로로 자동 정리했어요.');
  };

  @action removeCustomUrl = (idx) => {
    const cur = (this.editingPopup.display && this.editingPopup.display.customUrls) || [];
    this.setNested('display', 'customUrls', cur.filter((_, i) => i !== idx));
  };

  // 잘못 저장된 칩을 자동 정리
  @action fixCustomUrl = (idx) => {
    const cur = (this.editingPopup.display && this.editingPopup.display.customUrls) || [];
    const res = this.normalizeCustomUrl(cur[idx]);
    if (!res.ok) { toast.error(res.error); return; }
    const next = cur.slice();
    // 정리 후 중복되면 제거만
    if (next.includes(res.value)) next.splice(idx, 1);
    else next[idx] = res.value;
    this.setNested('display', 'customUrls', next);
    toast.success('"' + res.value + '" 로 정리했어요.');
  };

  @action savePopup = async () => {
    if (!this.editingPopup) return;
    if (!this.editingPopup.title || !this.editingPopup.title.trim()) {
      toast.error('제목을 입력하세요.');
      this.editTabIndex = 0;
      return;
    }
    this.isSaving = true;
    try {
      const data = {...this.editingPopup};
      delete data._id;
      const res = await this.props.savePopup({
        id: this.editingPopup._id || null,
        store_id: this.store._id,
        shopNo: this.store.shopNo || 1,
        data
      });
      if (res && res.data && res.data._id) {
        toast.success('팝업이 저장되었습니다.');
        this.closeEdit();
        this.reload();
      } else {
        throw new Error('저장 실패');
      }
    } catch (e) {
      toast.error(e.message || '저장에 실패했습니다.');
    } finally {
      this.isSaving = false;
    }
  };

  // === 리스트 액션 ===
  toggleActive = async (p) => {
    try {
      await this.props.togglePopup({id: p._id, store_id: this.store._id, isActive: !p.isActive});
      this.reload();
    } catch (e) { toast.error('상태 변경 실패'); }
  };
  duplicate = async (p) => {
    if (!window.confirm(`"${p.title}" 팝업을 복제할까요?`)) return;
    try {
      const res = await this.props.duplicatePopup({id: p._id, store_id: this.store._id});
      if (res && res.data && res.data._id) { toast.success('복제 완료'); this.reload(); }
    } catch (e) { toast.error('복제 실패'); }
  };
  removePopup = async (p) => {
    if (!window.confirm(`"${p.title}" 팝업을 삭제할까요? 되돌릴 수 없습니다.`)) return;
    try {
      await this.props.removePopup({id: p._id, store_id: this.store._id});
      toast.success('삭제 완료'); this.reload();
    } catch (e) { toast.error('삭제 실패'); }
  };

  // === 헬퍼 ===
  typeLabel = (v) => {
    const t = POPUP_TYPES.find(x => x.value === v);
    return t ? t.label : v;
  };
  popupStatus = (p) => {
    const now = new Date();
    if (!p.isActive) return {label: '비활성', color: '#9CA3AF', bg: '#F3F4F6'};
    if (p.startAt && new Date(p.startAt) > now) return {label: '예약', color: '#0891B2', bg: '#CFFAFE'};
    if (p.endAt && new Date(p.endAt) < now) return {label: '종료', color: '#B91C1C', bg: '#FEE2E2'};
    return {label: '노출중', color: '#065F46', bg: '#D1FAE5'};
  };
  formatPeriod = (p) => {
    const fmt = 'YY.MM.DD';
    if (!p.startAt && !p.endAt) return '상시 노출';
    const s = p.startAt ? moment(p.startAt).format(fmt) : '∞';
    const e = p.endAt ? moment(p.endAt).format(fmt) : '∞';
    return `${s} ~ ${e}`;
  };
  pagesLabel = (pages) => {
    if (!Array.isArray(pages) || pages.length === 0) return '모든 페이지';
    if (pages.length === PAGES.length) return '모든 페이지';
    return pages.map(p => (PAGES.find(x => x.value === p) || {label: p}).label).join(', ');
  };

  render() {
    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box pop-page">

          <div className="pop-header">
            <div className="pop-header__title">
              <p>쇼핑몰에 노출되는 팝업(이벤트, 쿠폰, 공지)을 만들고 관리합니다.</p>
            </div>
            <div className="pop-header__cta">
              <Button theme="primary" onClick={this.openCreate}>
                <FeatherIcon icon="plus" size="14"/><span> 팝업 만들기</span>
              </Button>
            </div>
          </div>

          <div className="pop-toolbar">
            <div className="pop-toolbar__filters">
              <div className="pop-search">
                <FeatherIcon icon="search" size="14"/>
                <FormInput placeholder="팝업 제목/메모 검색" value={this.keyword} onChange={this.onKeywordChange}/>
              </div>
            </div>
            <div className="pop-toolbar__chips">
              {STATUS_FILTERS.map(s => (
                <button key={s.value || 'all'}
                        className={`pop-chip ${this.filterStatus === s.value ? 'is-on' : ''}`}
                        onClick={() => this.setStatusFilter(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="pop-toolbar__meta">
              전체 <b>{this.totalCount}</b>개
            </div>
          </div>

          {this.isLoading ? (
            <div className="pop-state"><FeatherIcon icon="loader" size="36"/><div>불러오는 중…</div></div>
          ) : this.list.length === 0 ? (
            <div className="pop-state">
              <FeatherIcon icon="bell" size="48"/>
              <div className="pop-state__text">{this.keyword || this.filterStatus ? '조건에 맞는 팝업이 없습니다.' : '등록된 팝업이 없습니다.'}</div>
              {!(this.keyword || this.filterStatus) && (
                <Button theme="primary" size="sm" onClick={this.openCreate}>
                  <FeatherIcon icon="plus" size="14"/><span> 첫 팝업 만들기</span>
                </Button>
              )}
            </div>
          ) : (
            <div className="pop-grid">
              {this.list.map((p) => {
                const status = this.popupStatus(p);
                return (
                  <div key={p._id}
                       className={`pop-card ${!p.isActive ? 'is-off' : ''}`}>
                    <div className="pop-card__cover" onClick={() => this.openEdit(p)}>
                      <div className="pop-card__cover-frame">
                        <DeviceFrame device="mobile" popup={p} scale={0.18}/>
                      </div>
                      <span className="pop-card__status" style={{color: status.color, background: status.bg}}>
                        {status.label}
                      </span>
                    </div>
                    <div className="pop-card__body">
                      <div className="pop-card__title" title={p.title}>{p.title}</div>
                      <div className="pop-card__meta">
                        <span className="pop-tag">{this.typeLabel(p.type)}</span>
                        <span className="pop-tag pop-tag--pages">{this.pagesLabel(p.display && p.display.pages)}</span>
                      </div>
                      <div className="pop-card__period">
                        <FeatherIcon icon="calendar" size="11"/> {this.formatPeriod(p)}
                      </div>
                      {p.stats && (p.stats.impressions > 0 || p.stats.clicks > 0) && (
                        <div className="pop-card__stats">
                          <span><FeatherIcon icon="eye" size="11"/> {p.stats.impressions || 0}</span>
                          <span><FeatherIcon icon="mouse-pointer" size="11"/> {p.stats.clicks || 0}</span>
                          {p.stats.impressions > 0 && (
                            <span>CTR {((p.stats.clicks || 0) / p.stats.impressions * 100).toFixed(1)}%</span>
                          )}
                        </div>
                      )}
                      <div className="pop-card__actions">
                        <button className="pop-iconbtn" onClick={() => this.toggleActive(p)} title={p.isActive ? '비활성화' : '활성화'}>
                          <FeatherIcon icon={p.isActive ? 'toggle-right' : 'toggle-left'} size="16"/>
                        </button>
                        <button className="pop-iconbtn" onClick={() => this.openEdit(p)} title="편집">
                          <FeatherIcon icon="edit-2" size="14"/>
                        </button>
                        <button className="pop-iconbtn" onClick={() => this.duplicate(p)} title="복제">
                          <FeatherIcon icon="copy" size="14"/>
                        </button>
                        <button className="pop-iconbtn pop-iconbtn--danger" onClick={() => this.removePopup(p)} title="삭제">
                          <FeatherIcon icon="trash-2" size="14"/>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {this.totalPages > 1 && (
            <div className="pop-pager">
              <Button size="sm" theme="light" disabled={this.page <= 1} onClick={() => this.goPage(this.page - 1)}>
                <FeatherIcon icon="chevron-left" size="14"/>
              </Button>
              <span>{this.page} / {this.totalPages}</span>
              <Button size="sm" theme="light" disabled={this.page >= this.totalPages} onClick={() => this.goPage(this.page + 1)}>
                <FeatherIcon icon="chevron-right" size="14"/>
              </Button>
            </div>
          )}
        </div>

        {/* === 편집 모달 === */}
        {this.showEditModal && this.editingPopup && (
          <Modal open={this.showEditModal} toggle={() => {}} size="lg" className="pop-modal">
            <ModalHeader toggle={this.closeEdit}>
              <span>{this.editingPopup._id ? '팝업 편집' : '팝업 추가'}</span>
              <div className="pop-modemix">
                <button type="button"
                        className={`pop-modebtn ${this.editMode === 'simple' ? 'is-on' : ''}`}
                        onClick={() => this.setEditMode('simple')}
                        title="필수 항목만 빠르게">
                  <FeatherIcon icon="zap" size="12"/> 쉬운 모드
                </button>
                <button type="button"
                        className={`pop-modebtn ${this.editMode === 'advanced' ? 'is-on' : ''}`}
                        onClick={() => this.setEditMode('advanced')}
                        title="모든 세부 옵션">
                  <FeatherIcon icon="sliders" size="12"/> 심화 모드
                </button>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="pop-modal__layout pop-modal__layout--stacked">
                {/* 상: 미리보기 */}
                <div className="pop-modal__preview">
                  <div className="pop-modal__previewhead">
                    <span><FeatherIcon icon="eye" size="13" style={{verticalAlign:'-2px',marginRight:4}}/>미리보기</span>
                    <div className="pop-modal__devices">
                      {Object.entries(DEVICE_FRAMES).map(([k, v]) => (
                        <button key={k}
                                className={`pop-devbtn ${this.previewDevice === k ? 'is-on' : ''}`}
                                onClick={() => this.previewDevice = k}
                                title={v.label}>
                          <FeatherIcon icon={v.icon} size="14"/>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pop-modal__previewstage" ref={this.attachAreaRef}>
                    <DeviceFrame device={this.previewDevice} popup={this.editingPopup} scale={this.previewScale()}/>
                  </div>
                </div>
                {/* 하: 설정 화면 (모드별 분기) */}
                <div className="pop-modal__form">
                  <div className="pop-modal__formhead">
                    <span><FeatherIcon icon="settings" size="13" style={{verticalAlign:'-2px',marginRight:4}}/>설정 화면</span>
                  </div>
                  {this.editMode === 'simple' ? (
                    <div className="pop-tabpanel pop-tabpanel--simple">
                      {this.renderSimpleForm()}
                    </div>
                  ) : (
                    <>
                      <div className="pop-tabs">
                        {['기본 정보', '콘텐츠', '노출 위치', '트리거', '빈도', '일정/액션'].map((label, i) => (
                          <button key={i}
                                  className={`pop-tab ${this.editTabIndex === i ? 'is-on' : ''}`}
                                  onClick={() => this.editTabIndex = i}>
                            {i + 1}. {label}
                          </button>
                        ))}
                      </div>
                      <div className="pop-tabpanel">
                        {this.editTabIndex === 0 && this.renderTabBasic()}
                        {this.editTabIndex === 1 && this.renderTabContent()}
                        {this.editTabIndex === 2 && this.renderTabDisplay()}
                        {this.editTabIndex === 3 && this.renderTabTrigger()}
                        {this.editTabIndex === 4 && this.renderTabFrequency()}
                        {this.editTabIndex === 5 && this.renderTabSchedule()}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button theme="light" onClick={this.closeEdit}>취소</Button>
              <Button theme="primary" disabled={this.isSaving} onClick={this.savePopup}>
                <FeatherIcon icon={this.isSaving ? 'loader' : 'save'} size="14"/>
                <span> {this.isSaving ? '저장 중…' : '저장'}</span>
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </div>
    );
  }

  previewScale = () => {
    const f = DEVICE_FRAMES[this.previewDevice];
    if (!f) return 0.4;
    // 측정된 미리보기 영역 사용, 측정 전이면 fallback
    const padding = 24;  // 양쪽 16px 안전 여유
    const aw = Math.max((this.areaW || 1080) - padding, 200);
    const ah = Math.max((this.areaH || 540) - padding, 200);
    const sw = aw / f.w;
    const sh = ah / f.h;
    // 최대 1.0 (실 사이즈) — 영역이 충분히 크면 디바이스를 1:1로 표시
    return Math.min(sw, sh, 1);
  };

  // === 쉬운 모드 — 필수 필드만 단일 스크롤 뷰로 표시 ===
  // 쉬운 모드의 노출 페이지 프리셋 → display.pages 배열
  // 'all' = [], 'main' = ['main'], 'product' = ['product']
  getSimpleDisplayPreset() {
    const pages = (this.editingPopup && this.editingPopup.display && this.editingPopup.display.pages) || [];
    if (!pages.length) return 'all';
    if (pages.length === 1 && pages[0] === 'main') return 'main';
    if (pages.length === 1 && pages[0] === 'product') return 'product';
    return 'custom';   // 그 외 조합 — 심화 모드에서 설정된 값
  }

  @action setSimpleDisplayPreset = (preset) => {
    if (!this.editingPopup.display) this.editingPopup.display = {};
    if (preset === 'all') this.editingPopup.display.pages = [];
    else if (preset === 'main') this.editingPopup.display.pages = ['main'];
    else if (preset === 'product') this.editingPopup.display.pages = ['product'];
  };

  // 쉬운 모드 — 노출 위치(페이지 + 직접 지정 URL)를 사람이 읽을 수 있는 라벨로 (표시 전용, 변경은 심화 모드)
  getSimpleDisplayLabel() {
    const d = (this.editingPopup && this.editingPopup.display) || {};
    const pages = d.pages || [];
    const customUrls = d.customUrls || [];
    const LABELS = {main: '메인', category: '카테고리', product: '상품상세', cart: '장바구니', order: '결제', register: '회원가입', mypage: '마이페이지'};
    const parts = [];
    if (pages.length) parts.push(pages.map(p => LABELS[p] || p).join(', '));
    if (customUrls.length) parts.push('직접 지정 URL ' + customUrls.length + '개');
    return parts.length ? parts.join(' + ') : '모든 페이지';
  }

  // 캡션 ↔ 제목 동기화 — 쉬운 모드에서 캡션 입력하면 제목도 같이 업데이트
  @action setCaptionAndTitle = (value) => {
    this.setTemplateData('caption', value);
    this.setField('title', value);
  };

  renderSimpleForm() {
    const p = this.editingPopup;
    const td = p.templateData || {};
    const toLocal = (d) => d ? moment(d).format('YYYY-MM-DDTHH:mm') : '';
    const toDate = (s) => s ? new Date(s) : null;
    return (
      <div className="pop-simple">
        {/* 좌: 이미지 / 우: 캡션+노출페이지, 링크, 일정, 활성 */}
        <div className="pop-simple__grid">
          {/* 좌측 컬럼 */}
          <div className="pop-simple__col">
            {/* 이미지로만 노출하기 — 캡션·배경색을 무시하고 이미지만 노출 */}
            <div className="pop-field">
              <FormCheckbox toggle small checked={!!td.imageOnly}
                            onChange={() => this.setTemplateData('imageOnly', !td.imageOnly)}>
                이미지로만 노출하기
                <span className="pop-help" style={{marginLeft: 8, display: 'inline'}}>캡션과 배경색 없이 이미지만 노출됩니다</span>
              </FormCheckbox>
            </div>
            <div className="pop-field">
              <label>
                이미지
                <span className="pop-help" style={{marginLeft: 8, display: 'inline'}}>URL 입력 또는 파일 업로드 (5MB 이하)</span>
              </label>
              <div className="pop-imgupload">
                <FormInput value={td.imageUrl || ''} onChange={(e) => this.setTemplateData('imageUrl', e.target.value)}
                           placeholder="https://... (URL 직접 입력 또는 우측 버튼으로 업로드)"/>
                <Button theme="light" size="sm" disabled={this.isUploading}
                        onClick={() => this.triggerImageUpload()}>
                  <FeatherIcon icon={this.isUploading ? 'loader' : 'upload'} size="13"/>
                  <span style={{marginLeft: 4}}>{this.isUploading ? '업로드 중…' : '업로드'}</span>
                </Button>
                <input type="file" accept="image/*" ref={this.fileInputRef}
                       onChange={this.handleImageUpload}
                       style={{display: 'none'}}/>
              </div>
              {td.imageUrl && (
                <div className="pop-imgthumb">
                  <img src={td.imageUrl} alt="" onError={(e) => { e.target.style.display = 'none'; }}/>
                  <button type="button" className="pop-imgthumb__remove"
                          onClick={() => this.setTemplateData('imageUrl', '')}
                          title="이미지 제거">
                    <FeatherIcon icon="x" size="11"/>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 우측 컬럼 */}
          <div className="pop-simple__col">
            {/* 캡션 + 노출 페이지 (한 줄) — 캡션은 제목과 동기화, 제목 필드는 숨김 */}
            <div className="pop-row">
              <div className="pop-field" style={{flex: 2, minWidth: 0}}>
                <label>캡션 <span className="pop-help" style={{marginLeft: 8, display: 'inline'}}>{td.imageOnly ? '(이미지로만 노출 중 — 캡션 미노출)' : '(제목으로도 사용됩니다)'}</span></label>
                <FormInput value={td.caption || ''} disabled={!!td.imageOnly}
                           onChange={(e) => this.setCaptionAndTitle(e.target.value)}
                           placeholder="예) 봄 시즌 신상품 안내"/>
              </div>
              <div className="pop-field" style={{flex: 1, minWidth: 140}}>
                <label>노출 페이지 <span className="pop-help" style={{marginLeft: 6, display: 'inline'}}>(심화 모드에서 변경)</span></label>
                <FormInput value={this.getSimpleDisplayLabel()} disabled readOnly
                           title="노출 위치는 '심화 모드 &gt; 노출 위치' 탭에서 설정합니다."/>
              </div>
            </div>
            <div className="pop-field">
              <label>클릭 시 이동 URL</label>
              <FormInput value={td.linkUrl || ''} onChange={(e) => this.setTemplateData('linkUrl', e.target.value)}
                         placeholder="https://..."/>
            </div>
            <div className="pop-row">
              <div className="pop-field pop-field--half">
                <label>시작일시</label>
                <FormInput type="datetime-local" value={toLocal(p.startAt)}
                           onChange={(e) => this.setField('startAt', toDate(e.target.value))}/>
              </div>
              <div className="pop-field pop-field--half">
                <label>종료일시</label>
                <FormInput type="datetime-local" value={toLocal(p.endAt)}
                           onChange={(e) => this.setField('endAt', toDate(e.target.value))}/>
              </div>
            </div>
            <div className="pop-field">
              <FormCheckbox toggle checked={!!p.isActive} onChange={(e) => this.setField('isActive', e.target.checked)}>
                이 팝업을 사용합니다 (활성 상태)
              </FormCheckbox>
              <div className="pop-help">시작/종료일이 비어있으면 즉시 / 무기한 노출됩니다.</div>
            </div>
          </div>
        </div>

        {/* 안내 */}
        <div className="pop-simple__hint">
          <FeatherIcon icon="info" size="12"/>
          <span>크기 / 색상 / 카테고리 등 추가 노출 옵션 / 트리거 / 빈도 등 세부 설정은 상단 <strong>"심화 모드"</strong>에서 조정할 수 있습니다.</span>
        </div>
      </div>
    );
  }

  renderTabBasic() {
    const p = this.editingPopup;
    return (
      <div className="pop-fields">
        <div className="pop-field">
          <label>제목 <span className="pop-req">*</span></label>
          <FormInput value={p.title || ''} onChange={(e) => this.setField('title', e.target.value)}
                     placeholder="예) 신규 회원 환영 팝업"/>
        </div>
        <div className="pop-field">
          <label>메모</label>
          <FormTextarea rows="2" value={p.description || ''} onChange={(e) => this.setField('description', e.target.value)}
                        placeholder="관리용 메모 (노출되지 않음)"/>
        </div>
        <div className="pop-field">
          <label>유형</label>
          <div className="pop-typegrid">
            {POPUP_TYPES.map(t => (
              <button key={t.value}
                      className={`pop-typecard ${p.type === t.value ? 'is-on' : ''}`}
                      onClick={() => this.setType(t.value)}>
                <FeatherIcon icon={t.icon} size="18"/>
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="pop-row">
          <div className="pop-field pop-field--half">
            <label>위치</label>
            <FormSelect value={p.position} onChange={(e) => this.setField('position', e.target.value)}>
              {(POPUP_POSITIONS_BY_TYPE[p.type] || []).map(po => (
                <option key={po.v} value={po.v}>{po.l}</option>
              ))}
            </FormSelect>
          </div>
          <div className="pop-field pop-field--half">
            <label>그림자</label>
            <FormSelect value={p.shadow || 'lg'} onChange={(e) => this.setField('shadow', e.target.value)}>
              <option value="none">없음</option>
              <option value="sm">약함</option>
              <option value="md">보통</option>
              <option value="lg">강함</option>
            </FormSelect>
          </div>
        </div>
        <div className="pop-field">
          <label>노출 페이지 <span className="pop-help" style={{marginLeft: 8, display: 'inline'}}>(여러 페이지에 노출하려면 "노출 위치" 탭에서 세부 설정)</span></label>
          <FormSelect value={this.getSimpleDisplayPreset()} onChange={(e) => this.setSimpleDisplayPreset(e.target.value)}>
            <option value="all">모든 페이지</option>
            <option value="main">메인 페이지</option>
            <option value="product">상세 페이지</option>
            {this.getSimpleDisplayPreset() === 'custom' && (
              <option value="custom">사용자 지정 (노출 위치 탭에서 설정됨)</option>
            )}
          </FormSelect>
        </div>
        <div className="pop-row">
          <div className="pop-field pop-field--half">
            <label>너비</label>
            <div className="pop-sizeinput">
              <FormInput type="number" min="0" value={p.width.value}
                         onChange={(e) => this.setSize('width', 'value', parseInt(e.target.value, 10) || 0)}/>
              <FormSelect value={p.width.unit} onChange={(e) => this.setSize('width', 'unit', e.target.value)}>
                <option value="px">px</option><option value="%">%</option>
              </FormSelect>
            </div>
          </div>
          <div className="pop-field pop-field--half">
            <label>높이 (0=auto)</label>
            <div className="pop-sizeinput">
              <FormInput type="number" min="0" value={p.height.value}
                         onChange={(e) => this.setSize('height', 'value', parseInt(e.target.value, 10) || 0)}/>
              <FormSelect value={p.height.unit} onChange={(e) => this.setSize('height', 'unit', e.target.value)}>
                <option value="px">px</option><option value="%">%</option>
              </FormSelect>
            </div>
          </div>
        </div>
        <div className="pop-row">
          <div className="pop-field pop-field--third">
            <label>라운드(px)</label>
            <FormInput type="number" min="0" value={p.borderRadius != null ? p.borderRadius : 12}
                       onChange={(e) => this.setField('borderRadius', parseInt(e.target.value, 10) || 0)}/>
          </div>
          <div className="pop-field pop-field--third">
            <label>배경</label>
            <input type="color"
                   value={p.bgColor && p.bgColor !== 'transparent' ? p.bgColor : '#FFFFFF'}
                   disabled={p.bgColor === 'transparent'}
                   onChange={(e) => this.setField('bgColor', e.target.value)}/>
            <label className="pop-bg-transparent">
              <input type="checkbox"
                     checked={p.bgColor === 'transparent'}
                     onChange={(e) => this.setField('bgColor', e.target.checked ? 'transparent' : '#FFFFFF')}/>
              투명 (배경 없음)
            </label>
          </div>
          <div className="pop-field pop-field--third">
            <label>강조</label>
            <input type="color" value={p.accentColor || '#4A6CF7'} onChange={(e) => this.setField('accentColor', e.target.value)}/>
          </div>
        </div>
      </div>
    );
  }

  renderTabContent() {
    const p = this.editingPopup;
    const td = p.templateData || {};
    return (
      <div className="pop-fields">
        <div className="pop-field">
          <label>템플릿</label>
          <div className="pop-tplgrid">
            {TEMPLATE_PRESETS.map(t => (
              <button key={t.key}
                      className={`pop-tplcard ${p.templateKey === t.key ? 'is-on' : ''}`}
                      onClick={() => this.setTemplateKey(t.key)}>
                <FeatherIcon icon={t.icon} size="18"/>
                <span className="pop-tplcard__label">{t.label}</span>
                <span className="pop-tplcard__desc">{t.desc}</span>
              </button>
            ))}
          </div>
        </div>
        {/* 템플릿별 입력 폼 */}
        {p.templateKey === 'text' && (
          <>
            <div className="pop-field"><label>제목</label>
              <FormInput value={td.heading || ''} onChange={(e) => this.setTemplateData('heading', e.target.value)}/></div>
            <div className="pop-field"><label>본문</label>
              <FormTextarea rows="3" value={td.body || ''} onChange={(e) => this.setTemplateData('body', e.target.value)}/></div>
          </>
        )}
        {p.templateKey === 'image' && (
          <>
            <div className="pop-field">
              <FormCheckbox toggle small checked={!!td.imageOnly}
                            onChange={() => this.setTemplateData('imageOnly', !td.imageOnly)}>
                이미지로만 노출하기
                <span className="pop-help" style={{marginLeft: 8, display: 'inline'}}>캡션과 배경색 없이 이미지만 노출됩니다</span>
              </FormCheckbox>
            </div>
            <div className="pop-field"><label>이미지 URL</label>
              <FormInput value={td.imageUrl || ''} onChange={(e) => this.setTemplateData('imageUrl', e.target.value)}
                         placeholder="https://..."/></div>
            <div className="pop-field"><label>캡션</label>
              <FormInput value={td.caption || ''} disabled={!!td.imageOnly} onChange={(e) => this.setTemplateData('caption', e.target.value)}/></div>
            <div className="pop-row">
              <div className="pop-field pop-field--half">
                <label>캡션 폰트 크기(px)</label>
                <FormInput type="number" min="1" disabled={!!td.imageOnly} value={td.captionFontSize != null ? td.captionFontSize : 13}
                           onChange={(e) => this.setTemplateData('captionFontSize', parseInt(e.target.value, 10) || 13)}/>
              </div>
              <div className="pop-field pop-field--half">
                <label>캡션 색상</label>
                <input type="color" disabled={!!td.imageOnly} value={td.captionColor || p.fgColor || '#1F2937'}
                       onChange={(e) => this.setTemplateData('captionColor', e.target.value)}/>
              </div>
            </div>
            <div className="pop-field"><label>클릭 시 이동 URL</label>
              <FormInput value={td.linkUrl || ''} onChange={(e) => this.setTemplateData('linkUrl', e.target.value)}
                         placeholder="https://..."/></div>
          </>
        )}
        {p.templateKey === 'coupon' && (
          <>
            <div className="pop-field"><label>제목</label>
              <FormInput value={td.heading || ''} onChange={(e) => this.setTemplateData('heading', e.target.value)}/></div>
            <div className="pop-field"><label>본문</label>
              <FormTextarea rows="2" value={td.body || ''} onChange={(e) => this.setTemplateData('body', e.target.value)}/></div>
            <div className="pop-field"><label>쿠폰 코드</label>
              <FormInput value={td.code || ''} onChange={(e) => this.setTemplateData('code', e.target.value)}/></div>
            <div className="pop-field"><label>버튼 라벨</label>
              <FormInput value={td.buttonLabel || ''} onChange={(e) => this.setTemplateData('buttonLabel', e.target.value)}/></div>
            <div className="pop-field"><label>버튼 URL</label>
              <FormInput value={td.buttonUrl || ''} onChange={(e) => this.setTemplateData('buttonUrl', e.target.value)}/></div>
          </>
        )}
        {p.templateKey === 'cta' && (
          <>
            <div className="pop-field"><label>제목</label>
              <FormInput value={td.heading || ''} onChange={(e) => this.setTemplateData('heading', e.target.value)}/></div>
            <div className="pop-field"><label>본문</label>
              <FormTextarea rows="2" value={td.body || ''} onChange={(e) => this.setTemplateData('body', e.target.value)}/></div>
            <div className="pop-field"><label>버튼 라벨</label>
              <FormInput value={td.buttonLabel || ''} onChange={(e) => this.setTemplateData('buttonLabel', e.target.value)}/></div>
            <div className="pop-field"><label>버튼 URL</label>
              <FormInput value={td.buttonUrl || ''} onChange={(e) => this.setTemplateData('buttonUrl', e.target.value)}
                         placeholder="https://..."/></div>
          </>
        )}
        {p.templateKey === 'form' && (
          <>
            <div className="pop-field"><label>제목</label>
              <FormInput value={td.heading || ''} onChange={(e) => this.setTemplateData('heading', e.target.value)}/></div>
            <div className="pop-field"><label>본문</label>
              <FormTextarea rows="2" value={td.body || ''} onChange={(e) => this.setTemplateData('body', e.target.value)}/></div>
            <div className="pop-field"><label>입력 placeholder</label>
              <FormInput value={td.placeholder || ''} onChange={(e) => this.setTemplateData('placeholder', e.target.value)}/></div>
            <div className="pop-field"><label>제출 버튼 라벨</label>
              <FormInput value={td.buttonLabel || ''} onChange={(e) => this.setTemplateData('buttonLabel', e.target.value)}/></div>
          </>
        )}
      </div>
    );
  }

  renderTabDisplay() {
    const p = this.editingPopup;
    const pages = p.display.pages || [];
    const devices = p.display.devices || [];
    return (
      <div className="pop-fields">
        {/* 노출 위치 지정 방식 — 페이지 유형 / 주소 직접. 둘은 함께 쓰면 AND(교집합)라 노출이 안 되므로 하나만 사용 */}
        <div className="pop-field">
          <label>노출 위치 지정 방식</label>
          <div className="pop-locmode">
            <label className={`pop-locmode__opt ${this.displayLocationMode === 'page' ? 'is-on' : ''}`}>
              <input type="radio" name="locmode" checked={this.displayLocationMode === 'page'}
                     onChange={() => this.setDisplayLocationMode('page')}/>
              <span className="pop-locmode__body">
                <span className="pop-locmode__ttl">페이지 유형으로 지정</span>
                <span className="pop-locmode__desc">메인·상품상세·장바구니 등 페이지 종류로 선택</span>
              </span>
            </label>
            <label className={`pop-locmode__opt ${this.displayLocationMode === 'url' ? 'is-on' : ''}`}>
              <input type="radio" name="locmode" checked={this.displayLocationMode === 'url'}
                     onChange={() => this.setDisplayLocationMode('url')}/>
              <span className="pop-locmode__body">
                <span className="pop-locmode__ttl">주소로 직접 지정</span>
                <span className="pop-locmode__desc">특정 페이지 주소(경로)를 직접 입력</span>
              </span>
            </label>
          </div>
        </div>

        {this.displayLocationMode === 'page' && (
        <div className="pop-field">
          <label>노출 페이지 (선택 안 하면 모든 페이지)</label>
          <div className="pop-chipgrid">
            {PAGES.map(pg => (
              <button key={pg.value}
                      className={`pop-chip ${pages.includes(pg.value) ? 'is-on' : ''}`}
                      onClick={() => this.togglePage(pg.value)}>
                {pg.label}
              </button>
            ))}
          </div>
        </div>
        )}

        {this.displayLocationMode === 'url' && (
        <div className="pop-field">
          <label>특정 페이지에만 노출
            <span className="pop-help" style={{marginLeft: 6, display: 'inline'}}>(노출할 페이지 주소를 하나씩 추가하세요)</span>
          </label>

          {/* 입력 행 — 도메인은 고정 프리픽스로 보여줘, 사용자는 뒤 경로만 입력 */}
          <div className="pop-url-inputrow">
            <span className="pop-url-prefix" title="쇼핑몰 주소(도메인)는 이미 포함돼요. 그 뒤 경로만 입력하세요.">
              https://내쇼핑몰
            </span>
            <FormInput className="pop-url-input"
                       value={this.customUrlDraft}
                       onChange={(e) => this.setCustomUrlDraft(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.addCustomUrl(); } }}
                       placeholder="/event/summer"/>
            <Button theme="primary" className="pop-url-addbtn" onClick={this.addCustomUrl}>추가</Button>
          </div>

          {/* 와일드카드(*)를 쉬운 말로 — 체크 시 내부적으로 * 부착 */}
          <label className="pop-url-suboption">
            <FormCheckbox small checked={this.customUrlIncludeSub} onChange={this.toggleCustomUrlIncludeSub}>
              이 주소로 <strong>시작하는 모든 페이지</strong>에 노출
              <span className="pop-help" style={{marginLeft: 6, display: 'inline'}}>
                예: <code>/event</code> → /event, /event2026, /event/summer 등 “/event”로 시작하는 주소 전부
              </span>
            </FormCheckbox>
          </label>

          {/* 검증 에러 */}
          {this.customUrlError && (
            <div className="pop-url-error">
              <FeatherIcon icon="alert-circle" size="13"/> {this.customUrlError}
            </div>
          )}

          {/* 추가된 주소 목록 */}
          {(p.display.customUrls || []).length > 0 && (
            <div className="pop-url-chips">
              {(p.display.customUrls || []).map((u, i) => {
                const issue = this.customUrlIssue(u);
                const isSub = !issue && String(u).endsWith('*');
                const shown = isSub ? String(u).slice(0, -1) : u;
                return (
                  <span key={i} className={`pop-url-chip ${issue ? 'is-error' : ''}`} title={issue || ''}>
                    {issue && <FeatherIcon icon="alert-triangle" size="12" className="pop-url-chip__warn"/>}
                    <span className="pop-url-chip__text">{shown || u}</span>
                    {isSub && <em className="pop-url-chip__sub">하위 전체</em>}
                    {issue && (
                      <button type="button" className="pop-url-chip__fix" onClick={() => this.fixCustomUrl(i)}>
                        고치기
                      </button>
                    )}
                    <button type="button" className="pop-url-chip__x" onClick={() => this.removeCustomUrl(i)} aria-label="삭제">×</button>
                  </span>
                );
              })}
            </div>
          )}

          {/* 입력 방법 안내 */}
          <div className="pop-url-guide">
            <div className="pop-url-guide__title"><FeatherIcon icon="info" size="12"/> 입력 방법</div>
            <ul>
              <li><b>쇼핑몰 주소(https://…)는 빼고</b> 그 뒤 경로만 입력해요. 예: <code>https://myshop.com/event/summer</code> → <code>/event/summer</code></li>
              <li>주소는 항상 <code>/</code> 로 시작해요. 전체 URL을 붙여넣어도 자동으로 정리해 드려요.</li>
              <li>여러 페이지에 노출하려면 하나씩 추가하세요. 한 페이지라도 매칭되면 팝업이 노출됩니다.</li>
            </ul>
          </div>
        </div>
        )}
        <div className="pop-field">
          <label>디바이스</label>
          <div className="pop-chipgrid">
            {[{v: 'pc', l: 'PC'}, {v: 'mobile', l: '모바일'}, {v: 'tablet', l: '태블릿'}].map(d => (
              <button key={d.v}
                      className={`pop-chip ${devices.includes(d.v) ? 'is-on' : ''}`}
                      onClick={() => this.toggleDevice(d.v)}>
                {d.l}
              </button>
            ))}
          </div>
        </div>
        <div className="pop-field">
          <label>회원 조건</label>
          <FormSelect value={p.display.audience || 'all'}
                      onChange={(e) => this.setNested('display', 'audience', e.target.value)}>
            <option value="all">모두</option>
            <option value="member">회원만</option>
            <option value="nonMember">비회원만</option>
          </FormSelect>
        </div>
      </div>
    );
  }

  renderTabTrigger() {
    const t = this.editingPopup.trigger;
    return (
      <div className="pop-fields">
        <div className="pop-field">
          <label>트리거 종류</label>
          <div className="pop-radiolist">
            {TRIGGERS.map(tr => (
              <label key={tr.value} className={`pop-radio ${t.type === tr.value ? 'is-on' : ''}`}>
                <input type="radio" name="trigger" checked={t.type === tr.value}
                       onChange={() => this.setNested('trigger', 'type', tr.value)}/>
                <span>{tr.label}</span>
              </label>
            ))}
          </div>
        </div>
        {t.type === 'delay' && (
          <div className="pop-field"><label>지연 시간 (초)</label>
            <FormInput type="number" min="0" step="1" value={Math.round((t.delayMs || 0) / 1000)}
                       onChange={(e) => this.setNested('trigger', 'delayMs', (parseInt(e.target.value, 10) || 0) * 1000)}/>
          </div>
        )}
        {t.type === 'scroll' && (
          <div className="pop-field"><label>스크롤 % (0-100)</label>
            <FormInput type="number" min="0" max="100" value={t.scrollPercent || 0}
                       onChange={(e) => this.setNested('trigger', 'scrollPercent', parseInt(e.target.value, 10) || 0)}/>
          </div>
        )}
        {t.type === 'click' && (
          <div className="pop-field"><label>CSS 셀렉터</label>
            <FormInput value={t.selector || ''}
                       onChange={(e) => this.setNested('trigger', 'selector', e.target.value)}
                       placeholder=".btn-promo, #header-cta"/>
          </div>
        )}
      </div>
    );
  }

  renderTabFrequency() {
    const f = this.editingPopup.frequency;
    return (
      <div className="pop-fields">
        <div className="pop-field">
          <label>재노출 모드</label>
          <div className="pop-radiolist">
            {FREQUENCIES.map(fr => (
              <label key={fr.value} className={`pop-radio ${f.mode === fr.value ? 'is-on' : ''}`}>
                <input type="radio" name="frequency" checked={f.mode === fr.value}
                       onChange={() => this.setNested('frequency', 'mode', fr.value)}/>
                <span>{fr.label}</span>
              </label>
            ))}
          </div>
        </div>
        {f.mode === 'interval' && (
          <div className="pop-field"><label>간격 (시간)</label>
            <FormInput type="number" min="1" value={f.intervalHours || 1}
                       onChange={(e) => this.setNested('frequency', 'intervalHours', parseInt(e.target.value, 10) || 1)}/>
          </div>
        )}
        <div className="pop-field">
          <label>닫기 옵션</label>
          <FormCheckbox checked={!!f.showDismissBtn}
                        onChange={(e) => this.setNested('frequency', 'showDismissBtn', e.target.checked)}>
            "오늘 하루 보지 않기" 체크박스 표시
          </FormCheckbox>
          {f.showDismissBtn && (
            <div style={{marginTop: 8, display: 'flex', gap: 8, alignItems: 'center'}}>
              <span style={{fontSize: 12, color: '#6B7280'}}>안 보기 기간:</span>
              <FormSelect value={f.dismissDays || 1}
                          onChange={(e) => this.setNested('frequency', 'dismissDays', parseInt(e.target.value, 10))}
                          style={{width: 120}}>
                <option value="1">1일</option>
                <option value="3">3일</option>
                <option value="7">7일</option>
                <option value="30">30일</option>
              </FormSelect>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderTabSchedule() {
    const p = this.editingPopup;
    const toLocal = (d) => d ? moment(d).format('YYYY-MM-DDTHH:mm') : '';
    const toDate = (s) => s ? new Date(s) : null;
    return (
      <div className="pop-fields">
        <div className="pop-field">
          <label>활성 상태</label>
          <FormCheckbox toggle checked={!!p.isActive} onChange={(e) => this.setField('isActive', e.target.checked)}>
            이 팝업을 사용합니다
          </FormCheckbox>
        </div>
        <div className="pop-row">
          <div className="pop-field pop-field--half">
            <label>시작일시</label>
            <FormInput type="datetime-local" value={toLocal(p.startAt)}
                       onChange={(e) => this.setField('startAt', toDate(e.target.value))}/>
            <div className="pop-help">비워두면 즉시 노출</div>
          </div>
          <div className="pop-field pop-field--half">
            <label>종료일시</label>
            <FormInput type="datetime-local" value={toLocal(p.endAt)}
                       onChange={(e) => this.setField('endAt', toDate(e.target.value))}/>
            <div className="pop-help">비워두면 무기한</div>
          </div>
        </div>
      </div>
    );
  }
}

export default Popup;
